import { makeAutoObservable, runInAction } from 'mobx'
import { getMyCounselorProfile } from 'api/all'

const PLATFORM_CEO_ROLES = new Set(['ceo', 'master', 'super_admin'])
const ORGANIZATION_MANAGER_ROLES = new Set(['admin'])

class CounselorSessionStore {
	profile = null
	loadStatus = 'idle' // idle | loading | success | error
	error = null
	_inflight = null

	constructor() {
		makeAutoObservable(this, { _inflight: false }, { autoBind: true })
	}

	get roleName() {
		const name = this.profile?.role?.name
		return name ? String(name).toLowerCase() : ''
	}

	get isPlatformCeo() {
		return PLATFORM_CEO_ROLES.has(this.roleName)
	}

	get isOrganizationManager() {
		return ORGANIZATION_MANAGER_ROLES.has(this.roleName)
	}

	get canAccessUserManagement() {
		return this.isPlatformCeo || this.isOrganizationManager
	}

	get isMasterCounselor() {
		return this.canAccessUserManagement
	}

	get isLoaded() {
		return this.loadStatus === 'success'
	}

	async ensureLoaded({ force = false } = {}) {
		if (!force && this.loadStatus === 'success' && this.profile) {
			return this.profile
		}
		if (this._inflight) {
			return this._inflight
		}

		this.loadStatus = 'loading'
		this.error = null

		this._inflight = (async () => {
			try {
				const response = await getMyCounselorProfile()
				const data = response?.data ?? response
				runInAction(() => {
					this.profile = data || null
					this.loadStatus = 'success'
				})
				return data
			} catch (error) {
				runInAction(() => {
					this.profile = null
					this.loadStatus = 'error'
					this.error = error?.message || 'Failed to load counselor profile'
				})
				throw error
			} finally {
				this._inflight = null
			}
		})()

		return this._inflight
	}
}

export const counselorSessionStore = new CounselorSessionStore()
